import { useState, useRef, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Check,
  User,
  Palette,
  Bell,
  Key,
  Shield,
  Database,
  Camera,
  Eye,
  EyeOff,
  Sun,
  Moon,
  Monitor,
  Mail,
  Smartphone,
  Download,
  Trash2,
  AlertTriangle,
  RefreshCw,
  Lock,
  Globe
} from 'lucide-react';
import { useAppStore } from '../store/appStore';
import { SettingsSection, SettingsToggle } from '../components/settings';

export default function Settings() {
  const navigate = useNavigate();
  const { preferences, updatePreferences } = useAppStore();

  const [activeTab, setActiveTab] = useState<'profile' | 'appearance' | 'notifications' | 'api' | 'security' | 'data'>('profile');
  const [savedMessage, setSavedMessage] = useState(false);
  const [showGeminiKey, setShowGeminiKey] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Profile state
  const [profileData, setProfileData] = useState({
    name: 'Recipe Labs User',
    email: 'user@recipelabs.com',
    role: 'Enterprise Admin',
    avatar: ''
  });

  // API Keys state
  const [geminiKey, setGeminiKey] = useState('AIzaSyDemoKey1234567890abcdefghijklmnopqrs');
  const [keyValidation, setKeyValidation] = useState<'idle' | 'validating' | 'valid' | 'invalid'>('idle');

  // Theme state
  const [selectedTheme, setSelectedTheme] = useState<'light' | 'dark' | 'system'>('dark');
  const [accentColor, setAccentColor] = useState('#F5D547');

  // Notifications state
  const [notificationSettings, setNotificationSettings] = useState({
    emailEnabled: true,
    pushEnabled: true,
    appLaunches: true,
    systemUpdates: true,
    newApps: true,
    activitySummaries: false,
    weeklyDigest: true
  });

  // Security state
  const [securitySettings, setSecuritySettings] = useState({
    twoFactorEnabled: false,
    sessionTimeout: '30',
    activeSessions: 3
  });

  const handleSaveSettings = () => {
    setSavedMessage(true);
    setTimeout(() => setSavedMessage(false), 3000);
  };

  const handleAvatarChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileData({ ...profileData, avatar: reader.result as string });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleValidateGeminiKey = async () => {
    setKeyValidation('validating');
    // Simulate API validation
    await new Promise(resolve => setTimeout(resolve, 1500));
    const isValid = geminiKey.startsWith('AIza');
    setKeyValidation(isValid ? 'valid' : 'invalid');
    setTimeout(() => setKeyValidation('idle'), 3000);
  };

  const handleExportData = () => {
    // Simulate data export
    const data = {
      profile: profileData,
      preferences,
      exportDate: new Date().toISOString()
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `recipe-labs-data-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleClearCache = () => {
    localStorage.removeItem('recipe-labs-cache');
    alert('Cache cleared successfully!');
  };

  const handleDeleteAccount = () => {
    if (showDeleteConfirm) {
      alert('Account deletion process initiated. This is a demo.');
      setShowDeleteConfirm(false);
    } else {
      setShowDeleteConfirm(true);
      setTimeout(() => setShowDeleteConfirm(false), 5000);
    }
  };

  const navItems = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'appearance', label: 'Appearance', icon: Palette },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'api', label: 'API Keys', icon: Key },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'data', label: 'Data Management', icon: Database }
  ] as const;

  return (
    <div className="min-h-screen bg-recipe-bg">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-recipe-card border-b border-recipe-border backdrop-blur-sm bg-opacity-95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-recipe-text hover:text-recipe-accent transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back to Dashboard</span>
            </button>

            <button
              onClick={handleSaveSettings}
              className="flex items-center gap-2 px-4 py-2 bg-recipe-accent text-recipe-bg rounded-lg hover:bg-recipe-accent/90 transition-all hover:scale-105 font-semibold"
            >
              {savedMessage ? (
                <>
                  <Check className="w-4 h-4" />
                  Saved!
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 animate-fade-in">
          <h1 className="text-3xl font-bold text-recipe-text mb-2">Settings</h1>
          <p className="text-recipe-text-muted">Manage your account, preferences, and integrations</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <nav className="space-y-1 bg-recipe-card border border-recipe-border rounded-xl p-2 sticky top-24">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`
                      w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-left
                      ${isActive
                        ? 'bg-recipe-accent text-recipe-bg shadow-lg shadow-recipe-accent/20'
                        : 'text-recipe-text hover:bg-recipe-bg hover:text-recipe-accent'
                      }
                    `}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-recipe-card border border-recipe-border rounded-xl p-6 md:p-8">

              {/* Profile Settings */}
              {activeTab === 'profile' && (
                <SettingsSection
                  title="Profile Settings"
                  description="Update your personal information and profile details"
                  icon={<User className="w-5 h-5" />}
                >
                  {/* Avatar Upload */}
                  <div className="flex items-start gap-6 pb-6 border-b border-recipe-border">
                    <div className="relative group">
                      <div className="w-24 h-24 rounded-full bg-recipe-bg border-2 border-recipe-border flex items-center justify-center overflow-hidden">
                        {profileData.avatar ? (
                          <img src={profileData.avatar} alt="Avatar" className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-12 h-12 text-recipe-text-muted" />
                        )}
                      </div>
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="absolute inset-0 rounded-full bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Camera className="w-6 h-6 text-white" />
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarChange}
                        className="hidden"
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-recipe-text mb-1">Profile Picture</h3>
                      <p className="text-xs text-recipe-text-muted mb-3">
                        Upload a new avatar. Recommended size: 400x400px
                      </p>
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="text-sm text-recipe-accent hover:text-recipe-accent/80 font-medium transition-colors"
                      >
                        Choose file
                      </button>
                    </div>
                  </div>

                  {/* Profile Fields */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-recipe-text mb-2">
                        Full Name
                      </label>
                      <input
                        type="text"
                        value={profileData.name}
                        onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                        className="w-full px-4 py-2.5 bg-recipe-bg border border-recipe-border rounded-lg text-recipe-text focus:outline-none focus:ring-2 focus:ring-recipe-accent/50 transition-all"
                        placeholder="Enter your full name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-recipe-text mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={profileData.email}
                        onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                        className="w-full px-4 py-2.5 bg-recipe-bg border border-recipe-border rounded-lg text-recipe-text focus:outline-none focus:ring-2 focus:ring-recipe-accent/50 transition-all"
                        placeholder="your.email@company.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-recipe-text mb-2">
                        Role
                      </label>
                      <input
                        type="text"
                        value={profileData.role}
                        disabled
                        className="w-full px-4 py-2.5 bg-recipe-bg/50 border border-recipe-border rounded-lg text-recipe-text-muted cursor-not-allowed"
                      />
                      <p className="text-xs text-recipe-text-muted mt-2 flex items-center gap-1">
                        <Lock className="w-3 h-3" />
                        Contact your administrator to change your role
                      </p>
                    </div>
                  </div>
                </SettingsSection>
              )}

              {/* Appearance Settings */}
              {activeTab === 'appearance' && (
                <SettingsSection
                  title="Appearance"
                  description="Customize how Recipe Labs Hub looks and feels"
                  icon={<Palette className="w-5 h-5" />}
                >
                  {/* Theme Selection */}
                  <div>
                    <label className="block text-sm font-medium text-recipe-text mb-3">
                      Theme Mode
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { value: 'light', label: 'Light', icon: Sun },
                        { value: 'dark', label: 'Dark', icon: Moon },
                        { value: 'system', label: 'System', icon: Monitor }
                      ].map((theme) => {
                        const Icon = theme.icon;
                        const isSelected = selectedTheme === theme.value;
                        return (
                          <button
                            key={theme.value}
                            onClick={() => setSelectedTheme(theme.value as typeof selectedTheme)}
                            className={`
                              flex flex-col items-center gap-2 px-4 py-4 rounded-lg border-2 transition-all
                              ${isSelected
                                ? 'border-recipe-accent bg-recipe-accent/10 text-recipe-accent scale-105'
                                : 'border-recipe-border hover:border-recipe-accent/50 text-recipe-text hover:scale-102'
                              }
                            `}
                          >
                            <Icon className="w-6 h-6" />
                            <span className="font-medium text-sm">{theme.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Accent Color Picker */}
                  <div>
                    <label className="block text-sm font-medium text-recipe-text mb-3">
                      Accent Color
                    </label>
                    <div className="flex items-center gap-4">
                      <input
                        type="color"
                        value={accentColor}
                        onChange={(e) => setAccentColor(e.target.value)}
                        className="w-16 h-16 rounded-lg cursor-pointer border-2 border-recipe-border hover:border-recipe-accent transition-colors"
                      />
                      <div className="flex-1">
                        <input
                          type="text"
                          value={accentColor}
                          onChange={(e) => setAccentColor(e.target.value)}
                          className="w-full px-4 py-2.5 bg-recipe-bg border border-recipe-border rounded-lg text-recipe-text focus:outline-none focus:ring-2 focus:ring-recipe-accent/50 font-mono"
                          placeholder="#F5D547"
                        />
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {['#F5D547', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'].map((color) => (
                        <button
                          key={color}
                          onClick={() => setAccentColor(color)}
                          className="w-10 h-10 rounded-lg border-2 border-recipe-border hover:scale-110 transition-transform"
                          style={{ backgroundColor: color }}
                          title={color}
                        />
                      ))}
                    </div>
                  </div>

                  {/* View Mode */}
                  <div>
                    <label className="block text-sm font-medium text-recipe-text mb-3">
                      Default View Mode
                    </label>
                    <div className="flex gap-3">
                      <button
                        onClick={() => updatePreferences({ viewMode: 'grid' })}
                        className={`
                          flex-1 px-4 py-3 rounded-lg border-2 transition-all
                          ${preferences.viewMode === 'grid'
                            ? 'border-recipe-accent bg-recipe-accent/10 text-recipe-accent'
                            : 'border-recipe-border hover:border-recipe-accent/50 text-recipe-text'
                          }
                        `}
                      >
                        <span className="font-medium">Grid View</span>
                      </button>
                      <button
                        onClick={() => updatePreferences({ viewMode: 'list' })}
                        className={`
                          flex-1 px-4 py-3 rounded-lg border-2 transition-all
                          ${preferences.viewMode === 'list'
                            ? 'border-recipe-accent bg-recipe-accent/10 text-recipe-accent'
                            : 'border-recipe-border hover:border-recipe-accent/50 text-recipe-text'
                          }
                        `}
                      >
                        <span className="font-medium">List View</span>
                      </button>
                    </div>
                  </div>

                  {/* Compact Mode Toggle */}
                  <SettingsToggle
                    label="Compact Mode"
                    description="Reduce spacing and padding for a denser layout"
                    checked={preferences.compactMode}
                    onChange={(checked) => updatePreferences({ compactMode: checked })}
                  />
                </SettingsSection>
              )}

              {/* Notifications Settings */}
              {activeTab === 'notifications' && (
                <SettingsSection
                  title="Notifications"
                  description="Manage how you receive notifications and updates"
                  icon={<Bell className="w-5 h-5" />}
                >
                  {/* Master Toggle */}
                  <SettingsToggle
                    icon={<Bell className="w-5 h-5" />}
                    label="Enable Notifications"
                    description="Receive notifications about app updates and activities"
                    checked={preferences.notificationsEnabled}
                    onChange={(checked) => updatePreferences({ notificationsEnabled: checked })}
                  />

                  <div className="border-t border-recipe-border pt-6">
                    <h3 className="text-sm font-semibold text-recipe-text mb-4">Notification Channels</h3>
                    <div className="space-y-4">
                      <SettingsToggle
                        icon={<Mail className="w-5 h-5" />}
                        label="Email Notifications"
                        description="Receive notifications via email"
                        checked={notificationSettings.emailEnabled}
                        onChange={(checked) => setNotificationSettings({ ...notificationSettings, emailEnabled: checked })}
                        disabled={!preferences.notificationsEnabled}
                      />
                      <SettingsToggle
                        icon={<Smartphone className="w-5 h-5" />}
                        label="Push Notifications"
                        description="Receive browser push notifications"
                        checked={notificationSettings.pushEnabled}
                        onChange={(checked) => setNotificationSettings({ ...notificationSettings, pushEnabled: checked })}
                        disabled={!preferences.notificationsEnabled}
                      />
                    </div>
                  </div>

                  <div className="border-t border-recipe-border pt-6">
                    <h3 className="text-sm font-semibold text-recipe-text mb-4">Notification Types</h3>
                    <div className="space-y-3">
                      <SettingsToggle
                        label="App Launches"
                        description="Get notified when apps are launched"
                        checked={notificationSettings.appLaunches}
                        onChange={(checked) => setNotificationSettings({ ...notificationSettings, appLaunches: checked })}
                        disabled={!preferences.notificationsEnabled}
                      />
                      <SettingsToggle
                        label="System Updates"
                        description="Important platform and security updates"
                        checked={notificationSettings.systemUpdates}
                        onChange={(checked) => setNotificationSettings({ ...notificationSettings, systemUpdates: checked })}
                        disabled={!preferences.notificationsEnabled}
                      />
                      <SettingsToggle
                        label="New Apps Available"
                        description="Be notified when new apps are added"
                        checked={notificationSettings.newApps}
                        onChange={(checked) => setNotificationSettings({ ...notificationSettings, newApps: checked })}
                        disabled={!preferences.notificationsEnabled}
                      />
                      <SettingsToggle
                        label="Activity Summaries"
                        description="Daily digest of your activity"
                        checked={notificationSettings.activitySummaries}
                        onChange={(checked) => setNotificationSettings({ ...notificationSettings, activitySummaries: checked })}
                        disabled={!preferences.notificationsEnabled}
                      />
                      <SettingsToggle
                        label="Weekly Digest"
                        description="Weekly summary email every Monday"
                        checked={notificationSettings.weeklyDigest}
                        onChange={(checked) => setNotificationSettings({ ...notificationSettings, weeklyDigest: checked })}
                        disabled={!preferences.notificationsEnabled}
                      />
                    </div>
                  </div>
                </SettingsSection>
              )}

              {/* API Keys Settings */}
              {activeTab === 'api' && (
                <SettingsSection
                  title="API Keys"
                  description="Manage your API keys for third-party integrations"
                  icon={<Key className="w-5 h-5" />}
                >
                  {/* Gemini API Key */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-recipe-text mb-2">
                        Google Gemini API Key
                      </label>
                      <div className="flex gap-2">
                        <div className="flex-1 relative">
                          <input
                            type={showGeminiKey ? 'text' : 'password'}
                            value={geminiKey}
                            onChange={(e) => setGeminiKey(e.target.value)}
                            className="w-full px-4 py-2.5 pr-12 bg-recipe-bg border border-recipe-border rounded-lg text-recipe-text focus:outline-none focus:ring-2 focus:ring-recipe-accent/50 font-mono text-sm"
                            placeholder="AIzaSy..."
                          />
                          <button
                            onClick={() => setShowGeminiKey(!showGeminiKey)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-recipe-card rounded transition-colors"
                          >
                            {showGeminiKey ? (
                              <EyeOff className="w-4 h-4 text-recipe-text-muted" />
                            ) : (
                              <Eye className="w-4 h-4 text-recipe-text-muted" />
                            )}
                          </button>
                        </div>
                        <button
                          onClick={handleValidateGeminiKey}
                          disabled={keyValidation === 'validating'}
                          className={`
                            px-4 py-2.5 rounded-lg font-medium transition-all whitespace-nowrap
                            ${keyValidation === 'validating'
                              ? 'bg-recipe-border text-recipe-text-muted cursor-wait'
                              : keyValidation === 'valid'
                                ? 'bg-green-500/10 text-green-500 border border-green-500/20'
                                : keyValidation === 'invalid'
                                  ? 'bg-red-500/10 text-red-500 border border-red-500/20'
                                  : 'bg-recipe-accent text-recipe-bg hover:bg-recipe-accent/90'
                            }
                          `}
                        >
                          {keyValidation === 'validating' ? 'Validating...' :
                           keyValidation === 'valid' ? 'Valid!' :
                           keyValidation === 'invalid' ? 'Invalid' :
                           'Validate'}
                        </button>
                      </div>
                      <p className="text-xs text-recipe-text-muted mt-2">
                        Used for AI-powered features. Get your key from{' '}
                        <a href="https://makersuite.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="text-recipe-accent hover:underline">
                          Google AI Studio
                        </a>
                      </p>
                    </div>

                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-blue-400 mb-2 flex items-center gap-2">
                        <Globe className="w-4 h-4" />
                        Security Best Practices
                      </h4>
                      <ul className="text-sm text-recipe-text-muted space-y-1">
                        <li>• Never share your API keys publicly or commit them to version control</li>
                        <li>• Rotate your keys regularly for enhanced security</li>
                        <li>• Use environment variables to store keys in your applications</li>
                        <li>• Monitor your API usage for any suspicious activity</li>
                        <li>• Set up usage limits and alerts in the provider's dashboard</li>
                      </ul>
                    </div>
                  </div>
                </SettingsSection>
              )}

              {/* Privacy & Security Settings */}
              {activeTab === 'security' && (
                <SettingsSection
                  title="Privacy & Security"
                  description="Manage your account security and privacy settings"
                  icon={<Shield className="w-5 h-5" />}
                >
                  {/* Two-Factor Authentication */}
                  <div className="bg-recipe-bg border border-recipe-border rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold text-recipe-text mb-1 flex items-center gap-2">
                          <Lock className="w-4 h-4" />
                          Two-Factor Authentication
                        </h3>
                        <p className="text-xs text-recipe-text-muted mb-3">
                          Add an extra layer of security to your account
                        </p>
                        {securitySettings.twoFactorEnabled ? (
                          <span className="inline-flex items-center gap-1 text-xs text-green-500 bg-green-500/10 px-2 py-1 rounded">
                            <Check className="w-3 h-3" />
                            Enabled
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-recipe-text-muted bg-recipe-border px-2 py-1 rounded">
                            <AlertTriangle className="w-3 h-3" />
                            Disabled
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => setSecuritySettings({ ...securitySettings, twoFactorEnabled: !securitySettings.twoFactorEnabled })}
                        className={`
                          px-4 py-2 rounded-lg font-medium text-sm transition-all
                          ${securitySettings.twoFactorEnabled
                            ? 'bg-red-500/10 text-red-500 hover:bg-red-500/20'
                            : 'bg-recipe-accent text-recipe-bg hover:bg-recipe-accent/90'
                          }
                        `}
                      >
                        {securitySettings.twoFactorEnabled ? 'Disable' : 'Enable'}
                      </button>
                    </div>
                  </div>

                  {/* Session Timeout */}
                  <div>
                    <label className="block text-sm font-medium text-recipe-text mb-3">
                      Session Timeout
                    </label>
                    <select
                      value={securitySettings.sessionTimeout}
                      onChange={(e) => setSecuritySettings({ ...securitySettings, sessionTimeout: e.target.value })}
                      className="w-full px-4 py-2.5 bg-recipe-bg border border-recipe-border rounded-lg text-recipe-text focus:outline-none focus:ring-2 focus:ring-recipe-accent/50"
                    >
                      <option value="15">15 minutes</option>
                      <option value="30">30 minutes</option>
                      <option value="60">1 hour</option>
                      <option value="120">2 hours</option>
                      <option value="240">4 hours</option>
                      <option value="never">Never</option>
                    </select>
                    <p className="text-xs text-recipe-text-muted mt-2">
                      Automatically log out after period of inactivity
                    </p>
                  </div>

                  {/* Active Sessions */}
                  <div className="bg-recipe-bg border border-recipe-border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-recipe-text">Active Sessions</h3>
                      <span className="text-sm text-recipe-accent font-medium">{securitySettings.activeSessions} active</span>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between py-2 border-b border-recipe-border last:border-0">
                        <div>
                          <p className="text-sm text-recipe-text font-medium">Current Session</p>
                          <p className="text-xs text-recipe-text-muted">MacBook Pro - Chrome - San Francisco, CA</p>
                        </div>
                        <span className="text-xs text-green-500">Active now</span>
                      </div>
                      <div className="flex items-center justify-between py-2 border-b border-recipe-border last:border-0">
                        <div>
                          <p className="text-sm text-recipe-text font-medium">iPhone 15 Pro</p>
                          <p className="text-xs text-recipe-text-muted">Safari - San Francisco, CA</p>
                        </div>
                        <button className="text-xs text-red-500 hover:text-red-400">Revoke</button>
                      </div>
                      <div className="flex items-center justify-between py-2">
                        <div>
                          <p className="text-sm text-recipe-text font-medium">iPad Air</p>
                          <p className="text-xs text-recipe-text-muted">Safari - Los Angeles, CA</p>
                        </div>
                        <button className="text-xs text-red-500 hover:text-red-400">Revoke</button>
                      </div>
                    </div>
                    <button className="w-full mt-4 px-4 py-2 bg-red-500/10 text-red-500 rounded-lg hover:bg-red-500/20 transition-colors text-sm font-medium">
                      Revoke All Other Sessions
                    </button>
                  </div>
                </SettingsSection>
              )}

              {/* Data Management Settings */}
              {activeTab === 'data' && (
                <SettingsSection
                  title="Data Management"
                  description="Export, manage, or delete your account data"
                  icon={<Database className="w-5 h-5" />}
                >
                  {/* Export Data */}
                  <div className="bg-recipe-bg border border-recipe-border rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold text-recipe-text mb-1 flex items-center gap-2">
                          <Download className="w-4 h-4" />
                          Export Your Data
                        </h3>
                        <p className="text-xs text-recipe-text-muted">
                          Download a copy of your account data, preferences, and activity
                        </p>
                      </div>
                      <button
                        onClick={handleExportData}
                        className="px-4 py-2 bg-recipe-accent text-recipe-bg rounded-lg hover:bg-recipe-accent/90 transition-all font-medium text-sm whitespace-nowrap ml-4"
                      >
                        Export Data
                      </button>
                    </div>
                  </div>

                  {/* Clear Cache */}
                  <div className="bg-recipe-bg border border-recipe-border rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold text-recipe-text mb-1 flex items-center gap-2">
                          <RefreshCw className="w-4 h-4" />
                          Clear Cache
                        </h3>
                        <p className="text-xs text-recipe-text-muted">
                          Clear cached data to free up space and resolve issues
                        </p>
                      </div>
                      <button
                        onClick={handleClearCache}
                        className="px-4 py-2 bg-recipe-border text-recipe-text rounded-lg hover:bg-recipe-border/70 transition-all font-medium text-sm whitespace-nowrap ml-4"
                      >
                        Clear Cache
                      </button>
                    </div>
                  </div>

                  {/* Delete Account */}
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold text-red-500 mb-1 flex items-center gap-2">
                          <Trash2 className="w-4 h-4" />
                          Delete Account
                        </h3>
                        <p className="text-xs text-recipe-text-muted mb-2">
                          Permanently delete your account and all associated data
                        </p>
                        {showDeleteConfirm && (
                          <p className="text-xs text-red-500 font-medium">
                            Are you sure? This action cannot be undone. Click again to confirm.
                          </p>
                        )}
                      </div>
                      <button
                        onClick={handleDeleteAccount}
                        className={`
                          px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap ml-4 transition-all
                          ${showDeleteConfirm
                            ? 'bg-red-500 text-white hover:bg-red-600'
                            : 'bg-red-500/10 text-red-500 hover:bg-red-500/20'
                          }
                        `}
                      >
                        {showDeleteConfirm ? 'Confirm Delete' : 'Delete Account'}
                      </button>
                    </div>
                  </div>
                </SettingsSection>
              )}

            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
